import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  useTheme,
  CircularProgress,
  InputAdornment,
  Divider,
  Grid,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '../../svg/CloseIcon';
import { useState, useEffect } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import StarsIcon from '../../svg/MemeverseIcons/StarsIcon';
import { generateMemes } from '../../utils/apis/memeverse';
import ImageItem from './imageItem';

interface GenerateMemesProps {
  setGenerateMemesVisible: (val: boolean) => void;
  onMemeGenerated: (memeUrl: string) => void;
  userId: string;
  content?: string;
}

const GenerateMemes: React.FC<GenerateMemesProps> = ({
  setGenerateMemesVisible,
  onMemeGenerated,
  userId,
  content
}) => {
  const [prompt, setPrompt] = useState<string>(content || '');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [generatedMemes, setGeneratedMemes] = useState<string[]>([]);
  const [hasGenerated, setHasGenerated] = useState<boolean>(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (content) {
      handleGenerateMeme()
    }
  }, [content]);

  const handleGenerateMeme = async () => {
    if (!prompt) return;
    if (prompt.length > 300) return;

    if (!isGenerating) {
      setIsGenerating(true);
      setGeneratedMemes([]);

      try {
        const response = await generateMemes(userId, prompt);
        const data = await response.json();

        if (data && data.memes) {
          setGeneratedMemes(data.memes.filter((url) => url !== ''));
        }

        setHasGenerated(true);
      } catch (error) {
        console.error('Error generating memes:', error);
      } finally {
        setIsGenerating(false);
      }
    }
  };

  const handleMemeClick = (memeUrl: string) => {
    onMemeGenerated(memeUrl);
    setGenerateMemesVisible(false);
  };

  const handlePromptChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPrompt(e.target.value);
    if (hasGenerated) {
      setHasGenerated(false);
    }
  };

  const isCharacterLimitExceeded = prompt.length > 300;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: { xs: 2, md: 3 },
        height: '100%',
        overflow: 'auto',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant='h7' color='text.primary'>
          Generate Meme
        </Typography>
        <IconButton
          onClick={() => setGenerateMemesVisible(false)}
          sx={{
            stroke: theme.palette.action.active,
          }}
        >
          <CloseIcon strokeWidth={2} stroke='inherit' />
        </IconButton>
      </Box>

      <TextField
        value={prompt}
        onChange={handlePromptChange}
        placeholder='Describe the meme you want to create'
        fullWidth
        variant='outlined'
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <StarsIcon />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position='end'>
              <IconButton
                onClick={() => {
                  setPrompt('');
                  setHasGenerated(false);
                  setGeneratedMemes([]);
                }}
              >
                <ClearIcon />
              </IconButton>
            </InputAdornment>
          ),
          sx: {
            borderRadius: '50px',
            borderColor: theme.palette.divider,
            backgroundColor: theme.palette.background['paper-elevation-9'],
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: theme.palette.divider,
            },
          },
        }}
      />

      <Divider />

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          variant='body1'
          color={isCharacterLimitExceeded ? 'error' : 'primary'}
        >
          {prompt.length}/300
        </Typography>

        <Button
          variant='contained'
          color='primary'
          disabled={!prompt || isGenerating || hasGenerated || isCharacterLimitExceeded}
          onClick={handleGenerateMeme}
          startIcon={
            isGenerating ? <CircularProgress size={20} color='inherit' /> : null
          }
        >
          Generate
        </Button>
      </Box>

      {generatedMemes.length > 0 && (
        <>
          <Typography variant='subtitle1' sx={{ mt: 2 }}>
            Generated Memes
          </Typography>

          <Grid container spacing={2} columns={{ xs: 1, sm: 1, md: 2 }}>
            {generatedMemes.map((memeUrl, index) => (
              <Grid item xs={1} key={index}>
                <Box
                  sx={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'cover',
                    borderRadius: 1,
                    cursor: 'pointer',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'scale(1.02)',
                      boxShadow: theme.shadows[4],
                    },
                  }}
                  onClick={() => handleMemeClick(memeUrl)}
                >
                  <ImageItem
                    key={`meme-image-${index}`}
                    image={memeUrl}
                    alt={'meme image'}
                  />
                </Box>
              </Grid>
            ))}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default GenerateMemes;